/**
 * Les generateurs de francais: conjugaison, homophones, accords, vocabulaire.
 *
 * REGLE DU FICHIER: aucune phrase ne doit pouvoir devenir fausse par
 * substitution. Les modeles ne recoivent que des jetons dont on connait le
 * genre, le nombre et l'elision, et les determinants passent tous par les
 * fonctions de lexique.ts. Une faute de grammaire dans un jeu de grammaire est
 * pire que pas de jeu du tout — c'est la seule chose que l'enfant retiendra.
 */
import type { Alea } from '../alea.ts'
import type { Brouillon } from './atelier.ts'
import { composerChoix } from './atelier.ts'
import type { Adjectif, CategorieNom, Nom, Temps } from './lexique.ts'
import {
  accorder,
  accorderParticipe,
  adjectifsPour,
  AU_TEMPS,
  avecPronom,
  defini,
  formesAdjectif,
  formesParticipe,
  NOMS,
  PARTICIPES,
  PERSONNES,
  PRENOMS,
  tableau,
  VERBES,
} from './lexique.ts'

/** Premiere lettre en majuscule: un modele commence souvent par un determinant. */
function majuscule(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// ================================================================ conjugaison

const TOUS_LES_TEMPS: readonly Temps[] = ['present', 'imparfait', 'futur', 'passe-compose']

/**
 * Une forme a reconnaitre parmi les autres temps du MEME verbe et de la MEME
 * personne. Les distracteurs partagent donc le pronom: impossible de repondre
 * en lisant « nous » sans savoir ce qu'est un imparfait.
 */
export function conjugaison(tempsPermis: readonly Temps[], niveauVerbe: number): (a: Alea) => Brouillon {
  return (alea) => {
    const bassin = VERBES.filter((v) => v.niveau <= niveauVerbe)
    const v = alea.choix(bassin)
    /*
     * Le passe compose des verbes qui se conjuguent avec « etre » demande
     * l'accord du participe avec le sujet (« elles sont allées »). C'est un
     * autre exercice, et il existe: voir les accords. On l'ecarte ici plutot
     * que d'imposer un masculin arbitraire.
     */
    const utilisables = tempsPermis.filter((t) => t !== 'passe-compose' || v.aux === 'avoir')
    const t = utilisables.length > 0 ? alea.choix(utilisables) : 'present'
    const personne = alea.entier(0, 5)
    const forme = tableau(v, t)[personne] ?? ''
    const bon = avecPronom(personne, forme)

    const distracteurs: string[] = []
    for (const autre of TOUS_LES_TEMPS) {
      if (autre === t) continue
      if (autre === 'passe-compose' && v.aux === 'etre') continue
      distracteurs.push(avecPronom(personne, tableau(v, autre)[personne] ?? ''))
    }

    return {
      enonce: `Conjugue le verbe « ${v.infinitif} » ${AU_TEMPS[t]}, à la ${PERSONNES[personne] ?? ''}.`,
      consigne: 'Choisis la bonne forme.',
      saisie: 'choix',
      // La forme nue est acceptee aussi: elle ne coute rien et evite un refus
      // absurde si un jour la reponse se tape au clavier.
      reponses: [bon, forme],
      choix: composerChoix(alea, bon, alea.melanger(distracteurs)),
      taille: Math.min(9, v.rang),
    }
  }
}

// ================================================================= homophones

interface ModeleHomophone {
  /** Les boutons proposes, dans leur ordre d'apprentissage. */
  options: readonly string[]
  niveau: number
  /** La phrase, avec `___` pour le trou. `{prenom}` et `{noms}` se substituent. */
  phrase: string
  bon: string
  /** Categorie du nom a tirer pour `{noms}`. */
  cat?: CategorieNom
}

/**
 * Les paires d'homophones, du plus simple au plus retors.
 *
 * Aucun trou n'est en debut de phrase: il faudrait alors mettre une majuscule a
 * la reponse, et « On » ne serait plus le meme bouton que « on ».
 */
export const HOMOPHONES: readonly ModeleHomophone[] = [
  { options: ['a', 'à'], niveau: 1, phrase: '{prenom} ___ un beau vélo.', bon: 'a' },
  { options: ['a', 'à'], niveau: 1, phrase: 'Ce soir, nous allons ___ la piscine.', bon: 'à' },
  { options: ['a', 'à'], niveau: 1, phrase: '{prenom} pense ___ ses amis.', bon: 'à' },
  { options: ['a', 'à'], niveau: 1, phrase: 'Le chien ___ soif.', bon: 'a' },
  { options: ['et', 'est'], niveau: 1, phrase: 'Mon frère ___ très grand.', bon: 'est' },
  { options: ['et', 'est'], niveau: 1, phrase: 'J’aime les pommes ___ les poires.', bon: 'et' },
  { options: ['et', 'est'], niveau: 1, phrase: '{prenom} ___ dans le jardin.', bon: 'est' },
  { options: ['et', 'est'], niveau: 1, phrase: 'Il prend son sac ___ son manteau.', bon: 'et' },
  { options: ['on', 'ont'], niveau: 2, phrase: 'Ce matin, ___ va au parc.', bon: 'on' },
  { options: ['on', 'ont'], niveau: 2, phrase: 'Les enfants ___ faim.', bon: 'ont' },
  { options: ['on', 'ont'], niveau: 2, phrase: 'Quand ___ est prudent, tout va bien.', bon: 'on' },
  { options: ['on', 'ont'], niveau: 2, phrase: 'Mes cousins ___ un chien.', bon: 'ont' },
  { options: ['son', 'sont'], niveau: 3, phrase: '{prenom} range ___ sac.', bon: 'son' },
  { options: ['son', 'sont'], niveau: 3, phrase: 'Les {noms} ___ dans le jardin.', bon: 'sont', cat: 'animaux' },
  { options: ['son', 'sont'], niveau: 3, phrase: '{prenom} a perdu ___ crayon.', bon: 'son' },
  { options: ['son', 'sont'], niveau: 3, phrase: 'Ces livres ___ à moi.', bon: 'sont' },
  { options: ['ou', 'où'], niveau: 3, phrase: 'Tu veux du lait ___ du jus ?', bon: 'ou' },
  { options: ['ou', 'où'], niveau: 3, phrase: 'Je sais ___ tu habites.', bon: 'où' },
  { options: ['ou', 'où'], niveau: 3, phrase: 'Dis-moi ___ tu vas.', bon: 'où' },
  { options: ['ou', 'où'], niveau: 3, phrase: 'On prend le vélo ___ l’autobus ?', bon: 'ou' },
  { options: ['ces', 'ses'], niveau: 4, phrase: '{prenom} a rangé ___ jouets.', bon: 'ses' },
  { options: ['ces', 'ses'], niveau: 4, phrase: 'Regarde ___ nuages : il va pleuvoir.', bon: 'ces' },
  { options: ['ces', 'ses'], niveau: 4, phrase: '{prenom} met ___ bottes.', bon: 'ses' },
  { options: ['ces', 'ses'], niveau: 4, phrase: 'Je n’aime pas ___ couleurs-là.', bon: 'ces' },
  { options: ['la', 'là'], niveau: 4, phrase: 'Assieds-toi ___.', bon: 'là' },
  { options: ['la', 'là'], niveau: 4, phrase: 'Je vois ___ lune.', bon: 'la' },
  { options: ['la', 'là'], niveau: 4, phrase: 'Viens ___ tout de suite.', bon: 'là' },
  { options: ['la', 'là'], niveau: 4, phrase: 'Ferme ___ porte.', bon: 'la' },
  { options: ['ce', 'se'], niveau: 5, phrase: 'Depuis ___ matin, il pleut.', bon: 'ce' },
  { options: ['ce', 'se'], niveau: 5, phrase: '{prenom} ___ lave les mains.', bon: 'se' },
  { options: ['ce', 'se'], niveau: 5, phrase: 'Regarde ___ beau chien.', bon: 'ce' },
  { options: ['ce', 'se'], niveau: 5, phrase: 'Les oiseaux ___ cachent dans l’arbre.', bon: 'se' },
  { options: ['peu', 'peut'], niveau: 5, phrase: '{prenom} ___ courir très vite.', bon: 'peut' },
  { options: ['peu', 'peut'], niveau: 5, phrase: 'Il reste un ___ de gâteau.', bon: 'peu' },
  { options: ['peu', 'peut'], niveau: 5, phrase: 'Ce sac ___ contenir tous mes livres.', bon: 'peut' },
  { options: ['peu', 'peut'], niveau: 5, phrase: 'J’ai un ___ froid.', bon: 'peu' },
  { options: ['sa', 'ça'], niveau: 5, phrase: '{prenom} a perdu ___ tuque.', bon: 'sa' },
  { options: ['sa', 'ça'], niveau: 5, phrase: 'Franchement, ___ me plaît beaucoup.', bon: 'ça' },
  { options: ['sa', 'ça'], niveau: 5, phrase: 'Comment ___ va ?', bon: 'ça' },
  { options: ['sa', 'ça'], niveau: 5, phrase: 'Il range ___ chambre.', bon: 'sa' },
  { options: ['mes', 'mais'], niveau: 6, phrase: 'J’ai pris ___ gants.', bon: 'mes' },
  { options: ['mes', 'mais'], niveau: 6, phrase: 'Il pleut, ___ je sors quand même.', bon: 'mais' },
  { options: ['mes', 'mais'], niveau: 6, phrase: 'Tous ___ amis sont là.', bon: 'mes' },
  { options: ['mes', 'mais'], niveau: 6, phrase: 'C’est loin, ___ ça vaut la peine.', bon: 'mais' },
  { options: ['leur', 'leurs'], niveau: 6, phrase: 'Je ___ ai donné un bonbon.', bon: 'leur' },
  { options: ['leur', 'leurs'], niveau: 6, phrase: 'Les élèves rangent ___ crayons.', bon: 'leurs' },
  { options: ['leur', 'leurs'], niveau: 6, phrase: 'Je ___ parle souvent.', bon: 'leur' },
  { options: ['leur', 'leurs'], niveau: 6, phrase: 'Elles ont oublié ___ manteaux.', bon: 'leurs' },
  { options: ['quel', 'quelle', 'qu’elle'], niveau: 6, phrase: 'Dis-moi ___ heure il est.', bon: 'quelle' },
  { options: ['quel', 'quelle', 'qu’elle'], niveau: 6, phrase: 'Je crois ___ viendra demain.', bon: 'qu’elle' },
  { options: ['quel', 'quelle', 'qu’elle'], niveau: 6, phrase: 'Vraiment, ___ beau match !', bon: 'quel' },
  { options: ['quel', 'quelle', 'qu’elle'], niveau: 6, phrase: 'Il faut ___ finisse son travail.', bon: 'qu’elle' },
]

/**
 * Le bassin d'un niveau: ce qu'on vient d'apprendre et ce qu'on a appris juste
 * avant. Sans cette fenetre glissante, un enfant de 6e se verrait proposer
 * « a ou à », ce qui n'est plus un exercice mais une politesse.
 */
export function homophonesDuNiveau(niveau: number): readonly ModeleHomophone[] {
  const fenetre = HOMOPHONES.filter((m) => m.niveau <= niveau && m.niveau >= niveau - 1)
  return fenetre.length > 0 ? fenetre : HOMOPHONES.filter((m) => m.niveau <= niveau)
}

export function homophones(niveau: number): (a: Alea) => Brouillon {
  return (alea) => {
    const m = alea.choix(homophonesDuNiveau(niveau))
    let phrase = m.phrase
    if (phrase.includes('{prenom}')) phrase = phrase.replace('{prenom}', alea.choix(PRENOMS).nom)
    if (phrase.includes('{noms}')) {
      const n: Nom = alea.choix(NOMS[m.cat ?? 'animaux'])
      phrase = phrase.replace('{noms}', n.p)
    }
    return {
      enonce: phrase,
      consigne: 'Choisis le mot qui va dans le trou.',
      saisie: 'choix',
      reponses: [m.bon],
      choix: composerChoix(alea, m.bon, alea.melanger([...m.options].filter((o) => o !== m.bon))),
      taille: Math.min(9, m.niveau),
    }
  }
}

// ==================================================================== accords

/** Le sujet et l'adjectif s'accordent: c'est tout l'exercice. */
export function accordAdjectif(niveauAdjectif: number, avecFeminin: boolean): (a: Alea) => Brouillon {
  return (alea) => {
    const categorie: CategorieNom = alea.chance(0.5) ? 'animaux' : 'objets'
    // L'adjectif se choisit APRES le nom: c'est ce qui evite « la carte est heureuse ».
    const bassin = adjectifsPour(categorie === 'animaux' ? 'anime' : 'objet', niveauAdjectif)
    const adj: Adjectif = alea.choix(bassin)
    const tous = NOMS[categorie]
    const choisis = avecFeminin ? tous : tous.filter((n) => n.genre === 'm')
    const nom: Nom = alea.choix(choisis.length > 0 ? choisis : tous)
    const pluriel = alea.chance(0.5)
    const phrase = `${majuscule(defini(nom, pluriel))} ${pluriel ? 'sont' : 'est'} ___`
    const bon = accorder(adj, nom.genre, pluriel)
    return {
      enonce: `Complète : « ${phrase} ». Quelle est la bonne forme de l’adjectif « ${adj.ms} » ?`,
      consigne: 'Choisis la bonne forme.',
      saisie: 'choix',
      reponses: [bon],
      choix: composerChoix(alea, bon, alea.melanger(formesAdjectif(adj).filter((f) => f !== bon))),
      taille: Math.min(9, adj.niveau),
    }
  }
}

/**
 * Participe passe avec « avoir » et complement APRES le verbe: pas d'accord.
 * C'est le piege classique — l'enfant veut accorder avec le sujet.
 */
export function participeAvoir(alea: Alea): Brouillon {
  const p = alea.choix(PARTICIPES.filter((x) => x.aux === 'avoir'))
  const qui = alea.choix(PRENOMS)
  const bon = p.ms
  return {
    enonce: `Complète : « ${qui.nom} a ___ ${p.suite}. » Quelle est la bonne forme du verbe « ${p.infinitif} » ?`,
    consigne: 'Choisis la bonne forme.',
    saisie: 'choix',
    reponses: [bon],
    choix: composerChoix(alea, bon, alea.melanger(formesParticipe(p).filter((f) => f !== bon))),
    taille: 4,
  }
}

/** Participe passe avec « etre »: il s'accorde avec le sujet. */
export function participeEtre(alea: Alea): Brouillon {
  const p = alea.choix(PARTICIPES.filter((x) => x.aux === 'etre'))
  const genre: 'm' | 'f' = alea.chance(0.5) ? 'f' : 'm'
  const pluriel = alea.chance(0.5)
  const sujet = genre === 'm' ? (pluriel ? 'Ils' : 'Il') : pluriel ? 'Elles' : 'Elle'
  const bon = accorderParticipe(p, genre, pluriel)
  return {
    enonce: `Complète : « ${sujet} ${pluriel ? 'sont' : 'est'} ___ ${p.suite}. » Quelle est la bonne forme du verbe « ${p.infinitif} » ?`,
    consigne: 'Choisis la bonne forme.',
    saisie: 'choix',
    reponses: [bon],
    choix: composerChoix(alea, bon, alea.melanger(formesParticipe(p).filter((f) => f !== bon))),
    taille: 6,
  }
}

// ============================================================== vocabulaire

interface Couple {
  mot: string
  autre: string
  niveau: number
}

export const CONTRAIRES: readonly Couple[] = [
  { mot: 'grand', autre: 'petit', niveau: 1 },
  { mot: 'chaud', autre: 'froid', niveau: 1 },
  { mot: 'jour', autre: 'nuit', niveau: 1 },
  { mot: 'content', autre: 'triste', niveau: 1 },
  { mot: 'plein', autre: 'vide', niveau: 2 },
  { mot: 'propre', autre: 'sale', niveau: 2 },
  { mot: 'rapide', autre: 'lent', niveau: 2 },
  { mot: 'monter', autre: 'descendre', niveau: 3 },
  { mot: 'ouvrir', autre: 'fermer', niveau: 3 },
  { mot: 'devant', autre: 'derrière', niveau: 3 },
  { mot: 'facile', autre: 'difficile', niveau: 4 },
  { mot: 'toujours', autre: 'jamais', niveau: 4 },
]

export const SYNONYMES: readonly Couple[] = [
  { mot: 'beau', autre: 'joli', niveau: 3 },
  { mot: 'content', autre: 'heureux', niveau: 3 },
  { mot: 'drôle', autre: 'amusant', niveau: 3 },
  { mot: 'calme', autre: 'tranquille', niveau: 4 },
  { mot: 'fatigué', autre: 'épuisé', niveau: 4 },
  { mot: 'gentil', autre: 'aimable', niveau: 4 },
  { mot: 'maison', autre: 'demeure', niveau: 5 },
  { mot: 'difficile', autre: 'ardu', niveau: 5 },
  { mot: 'peur', autre: 'crainte', niveau: 5 },
  { mot: 'bizarre', autre: 'étrange', niveau: 6 },
  { mot: 'malin', autre: 'rusé', niveau: 6 },
  { mot: 'célèbre', autre: 'renommé', niveau: 6 },
]

/**
 * Le bassin d'un niveau. Le repli reste TOUJOURS dans « ce qu'on a deja vu »:
 * un repli sur la liste entiere proposait « bizarre / etrange » a un enfant de
 * 3e annee, ce qui n'est plus un exercice mais un piege.
 */
function coupleDuNiveau(liste: readonly Couple[], niveau: number): readonly Couple[] {
  const connus = liste.filter((c) => c.niveau <= niveau)
  const recents = connus.filter((c) => c.niveau >= niveau - 1)
  if (recents.length >= 3) return recents
  return connus.length >= 2 ? connus : liste
}

function questionCouple(titre: string, liste: readonly Couple[], niveau: number): (a: Alea) => Brouillon {
  return (alea) => {
    const bassin = coupleDuNiveau(liste, niveau)
    const c = alea.choix(bassin)
    const autres = alea.melanger(bassin.filter((x) => x.mot !== c.mot).map((x) => x.autre))
    return {
      enonce: `${titre} « ${c.mot} » ?`,
      consigne: 'Choisis le bon mot.',
      saisie: 'choix',
      reponses: [c.autre],
      choix: composerChoix(alea, c.autre, autres),
      taille: Math.min(9, c.niveau),
    }
  }
}

export function contraire(niveau: number): (a: Alea) => Brouillon {
  return questionCouple('Quel est le contraire de', CONTRAIRES, niveau)
}

export function synonyme(niveau: number): (a: Alea) => Brouillon {
  return questionCouple('Quel mot veut dire la même chose que', SYNONYMES, niveau)
}

interface Pluriel {
  s: string
  p: string
  faux: readonly string[]
  niveau: number
}

export const PLURIELS: readonly Pluriel[] = [
  { s: 'chat', p: 'chats', faux: ['chates', 'chatz', 'chatx'], niveau: 1 },
  { s: 'maison', p: 'maisons', faux: ['maisones', 'maisonx', 'maisonz'], niveau: 1 },
  { s: 'ami', p: 'amis', faux: ['amies', 'amix', 'amiz'], niveau: 1 },
  { s: 'fleur', p: 'fleurs', faux: ['fleures', 'fleurx', 'fleurz'], niveau: 2 },
  { s: 'cheval', p: 'chevaux', faux: ['chevals', 'chevales', 'chevaus'], niveau: 3 },
  { s: 'journal', p: 'journaux', faux: ['journals', 'journeaux', 'journaus'], niveau: 3 },
  { s: 'animal', p: 'animaux', faux: ['animals', 'animales', 'animaus'], niveau: 3 },
  { s: 'bateau', p: 'bateaux', faux: ['bateaus', 'bateauxs', 'bateax'], niveau: 3 },
  { s: 'genou', p: 'genoux', faux: ['genous', 'genouz', 'genouxs'], niveau: 4 },
  { s: 'hibou', p: 'hiboux', faux: ['hibous', 'hibouz', 'hibouxs'], niveau: 4 },
  { s: 'travail', p: 'travaux', faux: ['travails', 'travailx', 'travauls'], niveau: 4 },
  { s: 'vitrail', p: 'vitraux', faux: ['vitrails', 'vitrailx', 'vitrauls'], niveau: 5 },
]

export function pluriel(niveau: number): (a: Alea) => Brouillon {
  return (alea) => {
    const proches = PLURIELS.filter((m) => m.niveau <= niveau && m.niveau >= niveau - 1)
    const bassin = proches.length > 0 ? proches : PLURIELS.filter((m) => m.niveau <= niveau)
    const m = alea.choix(bassin.length > 0 ? bassin : PLURIELS)
    return {
      enonce: `Quel est le pluriel de « ${m.s} » ?`,
      consigne: 'Choisis la bonne orthographe.',
      saisie: 'choix',
      reponses: [m.p],
      choix: composerChoix(alea, m.p, alea.melanger([...m.faux])),
      taille: Math.min(9, m.niveau),
    }
  }
}

interface MotOrthographe {
  bon: string
  faux: readonly string[]
  niveau: number
}

export const ORTHOGRAPHE: readonly MotOrthographe[] = [
  { bon: 'école', faux: ['ecole', 'écolle', 'écolé'], niveau: 2 },
  { bon: 'petit', faux: ['peti', 'pettit', 'petitt'], niveau: 2 },
  { bon: 'chocolat', faux: ['chocola', 'chocolla', 'chaucolat'], niveau: 2 },
  { bon: 'toujours', faux: ['toujour', 'tousjours', 'toujourd'], niveau: 4 },
  { bon: 'beaucoup', faux: ['beaucou', 'baucoup', 'beacoup'], niveau: 4 },
  { bon: 'longtemps', faux: ['longtemp', 'lontemps', 'longtems'], niveau: 4 },
  { bon: 'aujourd’hui', faux: ['aujourdhui', 'aujourd’huit', 'aujourdui'], niveau: 6 },
  { bon: 'accueillir', faux: ['acceuillir', 'accueilir', 'aceuillir'], niveau: 6 },
  { bon: 'rythme', faux: ['rythm', 'rytme', 'rithme'], niveau: 6 },
  { bon: 'parallèle', faux: ['paralèle', 'parrallèle', 'paralléle'], niveau: 6 },
]

export function orthographe(niveau: number): (a: Alea) => Brouillon {
  return (alea) => {
    const proches = ORTHOGRAPHE.filter((m) => m.niveau <= niveau && m.niveau >= niveau - 2)
    const bassin = proches.length > 0 ? proches : ORTHOGRAPHE.filter((m) => m.niveau <= niveau)
    const m = alea.choix(bassin.length > 0 ? bassin : ORTHOGRAPHE)
    return {
      enonce: 'Quel mot est bien écrit ?',
      consigne: 'Choisis la bonne orthographe.',
      saisie: 'choix',
      reponses: [m.bon],
      choix: composerChoix(alea, m.bon, alea.melanger([...m.faux])),
      taille: Math.min(9, m.niveau),
    }
  }
}
